import React, { useState } from 'react';
import { Show } from '@refinedev/antd';
import { useNotification, useShow } from '@refinedev/core';
import { Alert, Button, Col, Input, Modal, Row, Space, theme } from 'antd';
import { AdminAccountDeletionRequestDto } from '@wellverse/shared-ts';
import { Field } from '../../components/Field';
import { API_BASE_URL, axiosInstance } from '../../providers/dataProvider';
import { StatusTag } from './labels';

export const AccountDeletionRequestShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<AdminAccountDeletionRequestDto>({ resource: 'account-deletion-requests' });
  const item = queryResult?.data?.data;
  const { open: notify } = useNotification();
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState<'approve' | 'reject'>();

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const review = async (action: 'approve' | 'reject') => {
    setBusy(action);
    try {
      await axiosInstance.post(`${API_BASE_URL}/account-deletion-request-admin/requests/${item.id}/${action}`, {
        adminNotes: notes || undefined,
      });
      notify?.({
        type: 'success',
        message: action === 'approve' ? 'Account deleted' : 'Request rejected',
      });
      await queryResult.refetch();
    } catch (error: any) {
      notify?.({
        type: 'error',
        message: error?.response?.data?.message ?? 'Could not update the request',
      });
    } finally {
      setBusy(undefined);
    }
  };

  // Approving deletes the account for good, so it always asks first.
  const confirmApprove = () =>
    Modal.confirm({
      title: 'Delete this account permanently?',
      content: `${item.userName ?? item.userId ?? 'The matched account'} and all of its data will be deleted. This cannot be undone.`,
      okText: 'Delete account',
      okButtonProps: { danger: true },
      onOk: () => review('approve'),
    });

  const pending = item.status === 'pending';

  return (
    <Show resource="account-deletion-requests" title={item.reference}>
      <Row gutter={32}>
        <Col xs={24} md={12}>
          <Field label="Reference">{item.reference}</Field>
          <Field label="Status">
            <StatusTag status={item.status} />
          </Field>
          {item.email && <Field label="Email">{item.email}</Field>}
          {item.phone && <Field label="Phone">{item.phone}</Field>}
          <Field label="Reason">
            <div style={{ whiteSpace: 'pre-wrap' }}>{item.reason || '—'}</div>
          </Field>
          <Field label="Matched account">
            {item.userId ? (
              <>
                {item.userName ? `${item.userName} · ` : ''}
                <code>{item.userId}</code>
              </>
            ) : (
              'No account matches this contact'
            )}
          </Field>
          <Field label="Received">{new Date(item.createdAt).toLocaleString()}</Field>
          {item.reviewedAt && (
            <Field label="Reviewed">
              {new Date(item.reviewedAt).toLocaleString()} · <code>{item.reviewedBy}</code>
            </Field>
          )}
          {!pending && item.adminNotes && (
            <Field label="Admin notes">
              <div style={{ whiteSpace: 'pre-wrap' }}>{item.adminNotes}</div>
            </Field>
          )}
        </Col>

        {pending && (
          <Col xs={24} md={12}>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Check that the person asking owns this account before approving. Approving deletes the account and its data immediately and emails the person (email requests only)."
            />
            <Field label="Admin notes">
              <Input.TextArea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
            <Space>
              <Button danger type="primary" onClick={confirmApprove} loading={busy === 'approve'} disabled={!!busy}>
                Approve and delete account
              </Button>
              <Button onClick={() => review('reject')} loading={busy === 'reject'} disabled={!!busy}>
                Reject
              </Button>
            </Space>
          </Col>
        )}
      </Row>
    </Show>
  );
};
